import React from "react";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FaMapMarkerAlt } from "react-icons/fa";
import { ArrowRight } from "lucide-react";
import RIASECHexagonChartDouble from "./RIASECHexagonChartDouble";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  Title,
  CategoryScale,
  LinearScale,
  BarElement
);

const ProgramDetailsModal = ({ show, onClose, program, userSurveyResults }) => {
  if (!show) return null;

  // Function to calculate color for the rec score text
  const getBackgroundGradient = (score) => {
    const solidGold = [255, 215, 0];
    const forestGreen = [34, 139, 34];
    const crimson = [220, 20, 60];

    let startColor, endColor, percentage;
    if (score >= 50) {
      startColor = solidGold;
      endColor = forestGreen;
      percentage = (score - 50) / 50;
    } else {
      startColor = crimson;
      endColor = solidGold;
      percentage = score / 50;
    }
    const interpolateColor = (start, end, factor) =>
      start.map((startComponent, index) =>
        Math.round(startComponent + factor * (end[index] - startComponent))
      );
    const [r, g, b] = interpolateColor(startColor, endColor, percentage);
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Prepare career outcomes pie data with percentages
  let careerPieData = [];
  if (program) {
    const c1 = program.Career1Percent * 100;
    const c2 = program.Career2Percent * 100;
    const c3 = program.Career3Percent * 100;
    const c4 = program.Career4Percent * 100;
    const sum = c1 + c2 + c3 + c4;
    const other = Math.max(0, 100 - sum);
    careerPieData = [
      { label: program.Career1, value: c1 },
      { label: program.Career2, value: c2 },
      { label: program.Career3, value: c3 },
      { label: program.Career4, value: c4 },
    ];
    if (other > 0) {
      careerPieData.push({ label: "Other", value: other });
    }
  }

  // Define colors for each slice
  const pieColors = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#888888",
  ];

  // Donut chart data
  const careerPieChartData = {
    labels: careerPieData.map((item) => item.label),
    datasets: [
      {
        data: careerPieData.map((item) => item.value),
        backgroundColor: pieColors.slice(0, careerPieData.length),
        hoverOffset: 4,
        borderWidth: 0,
      },
    ],
  };

  // Donut chart options
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "60%",
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const label = ctx.label || "";
            const value = ctx.raw || 0;
            return `${label}: ${value}%`;
          },
        },
      },
    },
  };

  // Bar chart data for average salary
  const barChartData = {
    labels: program
      ? [
          program.Career1,
          program.Career2,
          program.Career3,
          program.Career4,
        ]
      : [],
    datasets: [
      {
        label: "Average Salary",
        data: program
          ? [
              program.Career1AverageSalary,
              program.Career2AverageSalary,
              program.Career3AverageSalary,
              program.Career4AverageSalary,
            ]
          : [],
        backgroundColor: [
          "#0088FE",
          "#00C49F",
          "#FFBB28",
          "#FF8042",
        ],
      },
    ],
  };

  // Updated bar chart options with shadcn-inspired styling
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: "#4A5568",
          font: { family: "Inter, sans-serif", weight: "500" },
        },
        grid: { color: "#E2E8F0" },
      },
      x: {
        ticks: {
          color: "#4A5568",
          font: { family: "Inter, sans-serif", weight: "500" },
        },
        grid: { display: false },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    elements: {
      bar: {
        borderRadius: 4,
        borderSkipped: false,
        maxBarThickness: 30,
      },
    },
  };

  const generatedNumber = (program.PathwayName.length % 5) + 3;
  const highSchoolCredits = generatedNumber * 3;
  const savings = highSchoolCredits * 1000;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-md shadow-lg z-10 w-[65vw] max-h-[95vh] overflow-y-auto p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button positioned at the top right */}
        <button
          onClick={onClose}
          className="absolute top-1 right-1 text-2xl text-gray-600 hover:text-gray-800 transition-colors duration-200"
        >
          <FontAwesomeIcon className="w-6 h-6" icon={faTimes} />
        </button>

        {/* Header Section */}
        {program ? (
          <div className="">
          {/* Header Row: Title and Rec Score */}
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">
              {program.ProgramName} {program.PathwayType} Details
            </h2>
            <span className="text-lg font-semibold text-gray-800">
              Rec Score:{" "}
              <span style={{ color: getBackgroundGradient(program.recScore) }}>
                {Number(program.recScore).toFixed(2)}
              </span>
            </span>
          </div>
        
          {/* Row 1: Faculty/Terms (left) & Location (right) */}
          <div className="flex justify-between items-center mt-1">
            <div className="flex space-x-2 text-md text-gray-600">
              {program.Faculty && <span>{program.Faculty}</span>}
              {program.Terms && <span>| {program.Terms}</span>}
            </div>
            {program.Location && (
              <div className="flex items-center text-md text-gray-600">
                <FaMapMarkerAlt className="mr-1" />
                <span>{program.Location}</span>
              </div>
            )}
          </div>
        
          {/* Row 2: Conditionally render Delivery, Tuition details, and See More link */}
            {(program.CourseDelivery || program["Tuition-Domestic"] || program["Tuition-International"] || program.Link) && (
            <div className="flex justify-between border-[1.5px] border-gray-300 p-2 rounded-md text-sm items-center mt-2 text-md text-gray-700">
                <div>
                {program.CourseDelivery && (
                    <span><span className="font-semibold">Delivery:</span> {program.CourseDelivery}</span>
                )}
                </div>

                <div>
                {(program["Tuition-Domestic"] || program["Tuition-International"]) && (
                    <>
                    {program["Tuition-Domestic"] && (
                        <span><span className="font-semibold">Tuition (Domestic):</span> {program["Tuition-Domestic"]}</span>
                    )}

                    {program["Tuition-Domestic"] && program["Tuition-International"] && (
                        <span className="mx-2">|</span>
                    )}

                    {program["Tuition-International"] && (
                        <span>
                        <span className="font-semibold">Tuition (International):</span> {program["Tuition-International"]}
                        </span>
                    )}
                    </>
                )}
                </div>

                <div>
                {program.Link && (
                    <a
                    href={program.Link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline"
                    >
                    See More →
                    </a>
                )}
                </div>
            </div>
            )}
        
          {/* Description */}
          <div className="mt-1.5 border-[1.5px] border-gray-300 text-sm p-2 rounded-md text-gray-700">
            <span className="font-semibold">Description:</span> {program.ProgramDescription}
          </div>

          {/* Admission Requirements */}
          {program.AdmissionRequirement && (
            <div className="mt-1.5 border-[1.5px] border-gray-300 p-2 rounded-md text-gray-700">
            <div className="font-semibold mb-1">Admission Requirements</div>
            <div className="flex items-stretch space-x-4 text-sm">
  {/* Option 1 */}
  <div className="bg-green-100 py-2 pr-1 rounded-md flex-1 flex flex-col justify-center">
    <ul className="list-disc pl-5 space-y-1">
      <li>Credit in English Language Arts 30-1 or minimum 65% in English Language Arts 30-2 or equivalent</li>
      <li>Credit in Math 30-1 or Math 30-2 or equivalent</li>
    </ul>
  </div>
  <div className="font-semibold text-gray-700 text-lg self-center">OR</div>
  {/* Option 2 */}
  <div className="bg-gray-100 py-2 pr-1 rounded-md flex-1 flex flex-col justify-center">
    <ul className="list-disc pl-5 space-y-1">
      <li>Successful completion of the GED</li>
      <li>Language Arts: Reading and Writing: 520 minimum standard score</li>
      <li>Math Test: 520 minimum score</li>
    </ul>
  </div>
  <div className="font-semibold text-gray-700 text-lg self-center">OR</div>
  {/* Option 3 */}
  <div className="bg-gray-100 py-2 pr-1 rounded-md flex-1 flex flex-col justify-center">
    <ul className="list-disc pl-5 space-y-1">
      <li>Satisfactory results on the Bow Valley College Admissions Test</li>
    </ul>
  </div>
</div>


          </div>
          
            )}
        </div>
        
        ) : (
          <p className="text-gray-600">No program data available.</p>
        )}

        {/* Lower Section with Two Columns */}
        <div className="flex space-x-2 mt-2">
          {/* Left Column: Hexagon and Credit Breakdown */}
          <div className="w-1/2 space-y-2">
            <div className="border-[1.5px] border-gray-300 rounded-md p-2">
              <div className="font-semibold mb-3">
                Corresponding Interests
              </div>
              <div className="flex flex-col items-center justify-center">
                <RIASECHexagonChartDouble
                  results={program}
                  results2={userSurveyResults}
                />
              </div>
            </div>
            <div className="border-[1.5px] border-gray-300 rounded-md p-2">
              <div className="font-semibold mb-1">Credit Breakdown</div>
              <ul className="list-disc list-inside text-sm text-gray-700 pl-3">
                <li>
                  This pathway requires{" "}
                  <span className="font-semibold">{program.OverallCR} credits</span>,{" "}
                  <span className="font-semibold">{program.UDCR}</span> of which are upper-division.
                </li>
                <li>
                  <span className="font-semibold">{highSchoolCredits} high school credits</span> taken at Bow Valley High School transfer to this major.
                </li>
                <li>
                  You will be saving about{" "}
                  <span className="font-semibold">${savings}</span> by taking this major at Bow Valley College.
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column: Career Outcomes and Salary Ranges */}
          <div className="w-1/2 space-y-2">
            <div className="border-[1.5px] border-gray-300 rounded-md p-2">
              <div className="font-semibold mb-2">Career Outcomes</div>
              <div className="flex flex-col sm:flex-row items-start gap-2">
                <div className="relative w-28 h-28 sm:w-32 sm:h-32 flex-shrink-0 overflow-visible">
                  <Doughnut data={careerPieChartData} options={pieOptions} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-sm font-semibold">100%</span>
                    <span className="text-xs text-gray-600">Total</span>
                  </div>
                </div>
                <div className="min-w-[12.5vw] ml-5 text-xs sm:text-sm">
                  <div className="font-semibold mb-1">Outcomes Breakdown</div>
                  <div className="space-y-1">
                    {careerPieData.map((item, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div className="flex items-center">
                          <span
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: pieColors[index] }}
                          />
                          <span>{item.label}</span>
                        </div>
                        <span className="ml-2">{item.value}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="border-[1.5px] border-gray-300 rounded-md p-2">
              <div className="font-semibold mb-2">Starting Salary Ranges</div>
              {/* Updated Bar Chart Container with shadcn-inspired styling */}
              <div className="relative h-72 p-4 bg-white rounded-lg">
                <Bar data={barChartData} options={barOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramDetailsModal;
